import { useEffect, useState } from "react";
import { Download, FileText, MapPin, Building2, Home as HomeIcon } from "lucide-react";

/**
 * GuidesHub — homepage section that surfaces 26 downloadable PDF guides
 * (13 provinces × 2 roles: landlord + tenant). Each PDF is generated
 * server-side from /api/guides/{province}/{role}.pdf, calibrated with
 * province-specific notice periods, deposit caps, rent-increase
 * guidelines for the current year, and a maintenance breakdown.
 *
 * URLs are relative — both preview and production route `/api/*` to
 * the backend through the same ingress, so we don't depend on a build-time
 * env var matching the deployed host. Downloads use a native <a download>
 * anchor so the browser saves the PDF directly without any JS.
 */
export default function GuidesHub() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("landlord");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetch(`/api/guides/manifest`)
      .then((r) => r.json())
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]));
  }, []);

  // Show only the four biggest markets by default so the section stays
  // readable on mobile. Users tap "See all 13 provinces" to expand.
  const PRIMARY = new Set(["on", "bc", "ab", "qc"]);
  const visible = showAll
    ? items
    : items.filter((it) => PRIMARY.has((it.code || it.slug || "").toLowerCase()));

  return (
    <section id="guides" className="py-16 md:py-20 bg-gradient-to-br from-emerald-50/60 via-white to-sky-50/60 border-y border-slate-200" data-testid="guides-hub">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-700">Free downloads</div>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
            Province-specific rental + landlord guides. Free PDFs.
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
            One field guide for every Canadian province + territory — covering leasing procedures,
            notice periods, deposits, the {new Date().getFullYear()} rent-increase guideline, and
            maintenance fixes that prevent a board hearing. Updated yearly. No email required.
          </p>
        </div>

        {/* Role toggle */}
        <div className="mt-7 inline-flex p-1 rounded-full bg-white border border-slate-200 shadow-sm" data-testid="guides-role-toggle">
          {[
            { id: "landlord", label: "Landlord guides", icon: Building2 },
            { id: "tenant",   label: "Tenant guides",   icon: HomeIcon },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`guides-tab-${t.id}`}
                className={`px-4 sm:px-5 py-2 rounded-full text-sm font-bold inline-flex items-center gap-1.5 transition-colors
                  ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                <Icon className="w-3.5 h-3.5"/> {t.label}
              </button>
            );
          })}
        </div>

        {/* Grid of province cards — each card is a native <a download> for
            reliable browser-level download behaviour. */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="guides-grid">
          {items.length === 0 && (
            <div className="col-span-full text-center py-10 text-sm text-slate-400 border border-dashed border-slate-300 rounded-2xl bg-white/60">
              Loading provincial guides…
            </div>
          )}
          {visible.map((it) => {
            const tid = `guide-${tab}-${it.slug}`;
            const href = `${tab === "landlord" ? it.landlord_pdf : it.tenant_pdf}`;
            const filename = `central-rentals-${tab}-guide-${it.slug}.pdf`;
            return (
              <a key={it.slug} href={href} download={filename}
                target="_blank" rel="noopener noreferrer" data-testid={tid}
                className="text-left group p-3 rounded-xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
                  <FileText className="w-4 h-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold text-slate-500">
                    <MapPin className="w-2.5 h-2.5"/> {it.code} · {it.province}
                  </div>
                  <div className="font-display text-sm font-bold text-slate-900 mt-0.5 leading-snug truncate">
                    {tab === "landlord" ? "Landlord guide" : "Tenant guide"} — {it.province}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {it.rtb} · Rent cap: {it.rent_cap_2026}
                  </div>
                </div>
                <Download className="w-3.5 h-3.5 text-emerald-600 flex-none opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all mt-1"/>
              </a>
            );
          })}
        </div>

        {items.length > 4 && (
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="guides-toggle-all"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-200 hover:border-emerald-400 px-5 py-2.5 text-sm font-bold text-emerald-800 shadow-sm hover:shadow transition-all"
            >
              {showAll ? "Show top 4 markets only" : `See all ${items.length} provinces + territories`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
